import { useEffect, useMemo, useState } from "react";
import { Loader2, Mail, RotateCcw } from "lucide-react";
import { apiPost } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { RichTextEditor } from "@/components/shared/rich-text-editor";
import { cn } from "@/lib/utils";

export type EmailPreviewType = "reminder" | "ask_receipt" | "invite";

export interface EmailComposerProps {
  /** Display name of the recipient (read-only "To" field). */
  toName: string;
  /** Email address of the recipient (read-only hint). */
  toEmail?: string;
  /** Default subject prefilled into the editable subject input. */
  defaultSubject: string;
  /** Max subject length in characters. Defaults to 78 (mail-client soft truncation threshold). */
  subjectMaxLength?: number;
  /** Initial body content (plain text for plain mode, HTML for rich). */
  initialBody?: string;
  bodyMode?: "plain" | "rich";
  bodyLabel?: string;
  bodyPlaceholder?: string;
  /** Whether body is required before Send is enabled. */
  bodyRequired?: boolean;
  /** Preview params — these mirror the backend email-preview endpoint. */
  previewType: EmailPreviewType;
  previewTemplate?: string;
  clientId?: string;
  receiptId?: string;
  /** Actions */
  sending: boolean;
  onSend: (data: { subject: string; body: string }) => void | Promise<void>;
  onCancel: () => void;
  sendLabel?: string;
}

interface PreviewResponse {
  subject: string;
  html: string;
}

// Debounce helper — avoids firing preview requests on every keystroke.
// 600ms is slow enough that the preview doesn't re-render while the user is
// actively typing (which would otherwise reset their scroll position in the
// iframe every few characters).
function useDebounced<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

export function EmailComposer({
  toName,
  toEmail,
  defaultSubject,
  subjectMaxLength = 78,
  initialBody = "",
  bodyMode = "plain",
  bodyLabel = "Message",
  bodyPlaceholder,
  bodyRequired = false,
  previewType,
  previewTemplate,
  clientId,
  receiptId,
  sending,
  onSend,
  onCancel,
  sendLabel = "Send",
}: EmailComposerProps) {
  const [subject, setSubject] = useState(defaultSubject);
  const [body, setBody] = useState(initialBody);
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  // Reset subject / body when defaults change (template swap in reminder).
  useEffect(() => setSubject(defaultSubject), [defaultSubject]);
  useEffect(() => setBody(initialBody), [initialBody]);

  const debouncedSubject = useDebounced(subject, 600);
  const debouncedBody = useDebounced(body, 600);
  const debouncedTemplate = useDebounced(previewTemplate, 50);

  const previewKey = useMemo(
    () =>
      JSON.stringify({
        previewType,
        debouncedTemplate,
        clientId,
        receiptId,
        debouncedSubject,
        debouncedBody,
      }),
    [
      previewType,
      debouncedTemplate,
      clientId,
      receiptId,
      debouncedSubject,
      debouncedBody,
    ],
  );

  useEffect(() => {
    let cancelled = false;
    setPreviewLoading(true);
    setPreviewError(null);
    apiPost<PreviewResponse>("/api/accountant/email-preview", {
      type: previewType,
      template: previewTemplate,
      subject: debouncedSubject,
      body: debouncedBody,
      client_id: clientId,
      receipt_id: receiptId,
    })
      .then((data) => {
        if (cancelled) return;
        setPreview(data);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setPreviewError(err instanceof Error ? err.message : "Preview failed");
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewKey]);

  const subjectCount = subject.length;
  const subjectOver = subjectCount > subjectMaxLength;
  const resetSubject = () => setSubject(defaultSubject);

  const bodyIsBlank =
    bodyMode === "rich"
      ? !body.replace(/<[^>]+>/g, "").trim()
      : !body.trim();

  const sendDisabled =
    sending || subjectOver || (bodyRequired && bodyIsBlank);

  return (
    <div className="grid gap-4 md:grid-cols-2">
      {/* Composer */}
      <div className="space-y-3">
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">To</Label>
          <div className="text-sm">
            <span className="font-medium">{toName}</span>
            {toEmail && (
              <span className="ml-1 text-muted-foreground">&lt;{toEmail}&gt;</span>
            )}
          </div>
        </div>

        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label htmlFor="email-subject" className="text-xs">
              Subject
            </Label>
            <div className="flex items-center gap-2 text-[11px]">
              <span
                className={cn(
                  "tabular-nums",
                  subjectOver
                    ? "text-destructive"
                    : subjectCount > subjectMaxLength - 10
                      ? "text-warning"
                      : "text-muted-foreground",
                )}
              >
                {subjectCount}/{subjectMaxLength}
              </span>
              {subject !== defaultSubject && (
                <button
                  type="button"
                  onClick={resetSubject}
                  className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
                  title="Reset to default"
                >
                  <RotateCcw className="size-3" aria-hidden />
                  Reset
                </button>
              )}
            </div>
          </div>
          <Input
            id="email-subject"
            value={subject}
            onChange={(e) =>
              setSubject((e.target as HTMLInputElement).value)
            }
            maxLength={subjectMaxLength + 40}
            aria-invalid={subjectOver}
          />
          {subjectOver && (
            <p className="text-[11px] text-destructive">
              Subject is longer than {subjectMaxLength} characters. Mail
              clients will truncate it on mobile.
            </p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="email-body" className="text-xs">
            {bodyLabel}
            {!bodyRequired && (
              <span className="ml-1 text-muted-foreground">(optional)</span>
            )}
          </Label>
          {bodyMode === "rich" ? (
            <RichTextEditor
              value={body}
              onChange={setBody}
              placeholder={bodyPlaceholder}
              disabled={sending}
            />
          ) : (
            <Textarea
              id="email-body"
              value={body}
              onChange={(e) =>
                setBody((e.target as HTMLTextAreaElement).value)
              }
              rows={5}
              placeholder={bodyPlaceholder}
              disabled={sending}
            />
          )}
        </div>

        <div className="flex items-center justify-end gap-2 pt-1">
          <Button variant="outline" onClick={onCancel} disabled={sending}>
            Cancel
          </Button>
          <Button
            onClick={() => onSend({ subject, body })}
            disabled={sendDisabled}
          >
            {sending ? (
              <Loader2 className="mr-1.5 size-4 animate-spin" />
            ) : (
              <Mail className="mr-1.5 size-4" />
            )}
            {sending ? "Sending..." : sendLabel}
          </Button>
        </div>
      </div>

      {/* Preview */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <Label className="text-xs">Preview</Label>
          <span className="text-[11px] text-muted-foreground">
            This is what your client will see.
          </span>
        </div>
        <div className="rounded-md border bg-muted/30">
          {sending ? (
            // Hide the preview while sending so the dialog's close animation
            // doesn't flash a stale document on its way out (S11/S17).
            <div className="h-[min(60vh,560px)] flex items-center justify-center text-xs text-muted-foreground">
              Sending...
            </div>
          ) : previewLoading && !preview ? (
            <div className="space-y-2 p-4">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-8 w-28" />
            </div>
          ) : previewError ? (
            <div className="p-4 text-xs text-muted-foreground">
              Preview unavailable. The email will still send with the default
              template styling.
            </div>
          ) : (
            // Use srcDoc rather than contentDocument.write(): with an empty
            // sandbox the iframe is cross-origin to the parent and we can't
            // reach into its document. srcDoc works under all sandbox modes.
            <iframe
              title="Email preview"
              className="w-full h-[min(60vh,560px)] rounded-md bg-background"
              sandbox=""
              srcDoc={preview?.html ?? ""}
            />
          )}
        </div>
      </div>
    </div>
  );
}
